import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Button, Tooltip } from 'antd'
import { BodyText } from 'src/branding/typography'
import FadeIn from 'src/components/styled/FadeIn'
import GrowthEngineNudgeCard, {
  getNudgeCardPropsFromRecommendation,
} from 'src/components/growthEngine/GrowthEngineNudgeCard'
import SuccessHubBadge from 'src/components/growthEngine/SuccessHubBadge'
import SuccessHubBusinessDaysCard from 'src/components/growthEngine/SuccessHubBusinessDaysCard'
import SuccessHubCelebration from 'src/components/growthEngine/SuccessHubCelebration'
import SuccessHubBlackBelt from 'src/components/growthEngine/SuccessHubBlackBelt'
import SuccessHubCollections from 'src/components/growthEngine/SuccessHubCollections'
import SuccessHubMissionCard from 'src/components/growthEngine/SuccessHubMissionCard'
import SuccessHubMomentumStrip from 'src/components/growthEngine/SuccessHubMomentumStrip'
import SuccessHubProgressValueHero from 'src/components/growthEngine/SuccessHubProgressValueHero'
import OllieIcon from 'src/components/icons/OllieIcon'
import OllieAskLink from 'src/components/ollie/OllieAskLink'
import { useOllieAssistant } from 'src/components/ollie/OllieAssistantContext'
import OllieNoticedRows from 'src/components/ollie/OllieNoticedRows'
import useOllieAtLimit from 'src/hooks/useOllieAtLimit'
import useOllieFindings from 'src/hooks/useOllieFindings'
import { OLLIE_FINDING_SURFACES } from 'src/utils/ollieFindingsApi'
import {
  findOllieFinding,
  selectOllieFindingsState,
} from 'src/utils/ollieFindingsSelectors'
import { OLLIE_NOTICED_PAGES } from 'src/utils/ollieNoticedRows'
import {
  OLLIE_REFRESH_FOLLOW_UP,
  shouldFollowUpOllieRefresh,
} from 'src/utils/ollieRefreshFollowUp'
import {
  SUCCESS_HUB_OLLIE_PICK_CTA_MODES,
  SUCCESS_HUB_OLLIE_WINS_CHECK_ID,
  buildSuccessHubOlliePick,
  buildSuccessHubOllieWinReceipts,
  isSuccessHubOlliePickAction,
} from 'src/utils/successHubOllie'
import styled from 'styled-components/macro'
import {
  fetchGrowthEngineSuccessHub,
  postGrowthEngineAnalytics,
  postGrowthEngineRecommendationInteraction,
} from 'src/utils/growthEngineApi'
import { resolveGrowthEngineCtaPath } from 'src/utils/growthEngineCtaRoutes'
import { resolveProgressAskOllie } from 'src/utils/growthEngineProgressAskOllie'
import { isOllieDraftAskBlocked } from 'src/utils/ollieAskLimit'
import { pickNextStapleForPar } from 'src/utils/growthEngineSuggestedProduct'
import {
  getStockDashboardHonestNote,
  rewriteFreeStockDashboardCopy,
  shouldBlockStockStartCta,
} from 'src/utils/growthEngineStockHonestFirstTry'
import {
  GROWTH_ENGINE_SUCCESS_HUB_SURFACE,
  SUCCESS_HUB_BADGE_MARK_SIZE,
  buildSuccessHubMomentumStrip,
  buildSuccessHubProgressSummary,
  formatSuccessHubHealthFootnote,
  formatSuccessHubReceiptsMoreLabel,
  formatSuccessHubTileStat,
  formatSuccessHubAchievementSetProgress,
  formatSuccessHubTilesCue,
  isSuccessHubMatureBoard,
  isSuccessHubMatureTilesSection,
  mapSuccessHubHeroToRecommendation,
  planSuccessHubReceiptGrid,
  shouldCelebrateSuccessHubBlackBelt,
  shouldShowSuccessHubBlackBelt,
  shouldShowSuccessHubCelebration,
  shouldShowSuccessHubFoldCue,
  successHubTileCtaAction,
} from 'src/utils/growthEngineSuccessHub'
import {
  SUCCESS_HUB_PROGRESS_TOKENS,
  successHubProgressRowBorder,
  successHubProgressSurfaceBorder,
} from 'src/utils/successHubProgressTokens'

const T = SUCCESS_HUB_PROGRESS_TOKENS

/**
 * Progress — value shout + today’s kitchen job first, then calm momentum
 * (% + climb + bar), slim fold cue (rule + line), one Locked in section below.
 * Contract frozen at 29.39 — see dashboard/docs/successHubProgressFrozen.md.
 */
const Progress = () => {
  const history = useHistory()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = venue?._id
  const currentPlan = venue?.usageLimits?.currentPlan
  const venueProducts = useSelector(state => state.venueProducts?.products)
  const venueProductsLoading = useSelector(
    state => state.venueProducts?.isLoading,
  )
  const [hub, setHub] = useState(null)
  const [loadState, setLoadState] = useState('idle')
  const shownIntentRef = useRef(null)
  const shownCelebrationRef = useRef(null)
  const shownBlackBeltRef = useRef(null)
  const { openOllieWithFinding } = useOllieAssistant()
  const { venueId: ollieVenueId, markClicked: markOllieClicked } =
    useOllieFindings()
  const ollieFindings = useSelector(selectOllieFindingsState)
  const ollieAtLimit = useOllieAtLimit()

  const hubFollowUpRef = useRef(null)

  const loadHub = useCallback(
    async ({ followUp = 0 } = {}) => {
      if (!venueId) return
      const quiet = followUp > 0
      if (!quiet) setLoadState('loading')
      try {
        const next = await fetchGrowthEngineSuccessHub(venueId, {
          platform: 'dashboard',
          n: 3,
        })
        setHub(next)
        setLoadState('ready')
        if (
          shouldFollowUpOllieRefresh({
            refreshing: next?.ollieRefreshing,
            followUp,
          })
        ) {
          clearTimeout(hubFollowUpRef.current)
          hubFollowUpRef.current = setTimeout(
            () => loadHub({ followUp: followUp + 1 }),
            OLLIE_REFRESH_FOLLOW_UP.delayMs,
          )
        }
      } catch (err) {
        console.warn('Success Hub failed to load', err)
        if (!quiet) setLoadState('error')
      }
    },
    [venueId],
  )

  useEffect(() => {
    loadHub()
    return () => clearTimeout(hubFollowUpRef.current)
  }, [loadHub])

  useEffect(() => {
    const today = hub?.today
    if (!venueId || !today?.intentId) return
    if (shownIntentRef.current === today.intentId) return
    shownIntentRef.current = today.intentId
    const pickFingerprint =
      hub?.hero?.olliePick?.fingerprint || today.olliePick?.fingerprint || null
    postGrowthEngineRecommendationInteraction(venueId, {
      intentId: today.intentId,
      interaction: 'shown',
      featureId: today.featureId,
      platform: 'dashboard',
      metadata: {
        surface: GROWTH_ENGINE_SUCCESS_HUB_SURFACE,
        variantIndex: hub?.hero?.variantIndex ?? today.variantIndex,
        ...(pickFingerprint ? { fingerprint: pickFingerprint } : {}),
      },
    }).catch(err => {
      console.warn('Success Hub shown interaction failed', err)
    })
  }, [hub, venueId])

  const showCelebration = shouldShowSuccessHubCelebration(hub)
  const showBlackBelt = shouldShowSuccessHubBlackBelt(hub)
  const celebrateBlackBelt = shouldCelebrateSuccessHubBlackBelt(hub)

  useEffect(() => {
    const celebration = hub?.celebration
    if (!venueId || !showCelebration || !celebration?.achievementId) return
    if (shownCelebrationRef.current === celebration.achievementId) return
    shownCelebrationRef.current = celebration.achievementId
    postGrowthEngineAnalytics(venueId, {
      eventName: 'success_hub_celebration_viewed',
      featureId: celebration.featureId,
      platform: 'dashboard',
      metadata: {
        surface: GROWTH_ENGINE_SUCCESS_HUB_SURFACE,
        achievementId: celebration.achievementId,
      },
    }).catch(err => {
      console.warn('Success Hub celebration view failed', err)
    })
  }, [hub, venueId, showCelebration])

  useEffect(() => {
    const belt = hub?.blackBelt
    if (!venueId || !celebrateBlackBelt || !belt?.id) return
    if (shownBlackBeltRef.current === belt.id) return
    shownBlackBeltRef.current = belt.id
    postGrowthEngineAnalytics(venueId, {
      eventName: 'success_hub_celebration_viewed',
      platform: 'dashboard',
      metadata: {
        surface: GROWTH_ENGINE_SUCCESS_HUB_SURFACE,
        achievementId: belt.id,
      },
    }).catch(err => {
      console.warn('Success Hub Black Belt view failed', err)
    })
  }, [hub, venueId, celebrateBlackBelt])

  const recordAndGo = useCallback(
    item => {
      const action = item?.ctaAction || item?.cta?.action
      const intentId = item?.intentId
      if (!venueId || !intentId) return
      if (
        shouldBlockStockStartCta({
          action,
          featureId: item.featureId,
          plan: currentPlan,
        })
      ) {
        return
      }
      const ctaOptions = {
        shoppingListEmpty:
          action === 'open_orders' &&
          !venueProductsLoading &&
          Array.isArray(venueProducts) &&
          venueProducts.length === 0,
        fromProgress: true,
        featureId: item.featureId,
      }
      if (action === 'open_par_levels') {
        const staple = pickNextStapleForPar(venueProducts)
        if (staple) {
          ctaOptions.product = staple.title || staple.product?.title
          ctaOptions.productId = staple._id
        }
      }
      if (action === 'open_menu_builder') {
        const warningIntent = /approaching_limit|limit_reached/.test(
          String(intentId || ''),
        )
        if (!warningIntent) {
          ctaOptions.openCreate = true
        }
      }
      const path = resolveGrowthEngineCtaPath(venueId, action, ctaOptions)
      // Navigate first — recording the click must not busy-wait the CTA.
      if (path) history.push(path)
      postGrowthEngineRecommendationInteraction(venueId, {
        intentId,
        interaction: 'clicked',
        featureId: item.featureId,
        platform: 'dashboard',
        metadata: { surface: GROWTH_ENGINE_SUCCESS_HUB_SURFACE },
      }).catch(err => {
        console.warn('Success Hub clicked interaction failed', err)
      })
    },
    [history, venueId, venueProducts, venueProductsLoading, currentPlan],
  )

  const heroRecommendation = mapSuccessHubHeroToRecommendation(
    hub?.hero,
    hub?.today,
  )
  const heroCard = getNudgeCardPropsFromRecommendation(heroRecommendation, {
    currentPlan,
  })
  const heroAskOllie = resolveProgressAskOllie(
    heroRecommendation?.featureId,
    heroRecommendation?.askOllie,
  )
  const olliePick = buildSuccessHubOlliePick(heroRecommendation, { venueId })
  const showHeroAskOllie =
    Boolean(heroAskOllie) &&
    !isOllieDraftAskBlocked({
      checkId: olliePick?.checkId,
      atLimit: ollieAtLimit,
    })
  const ollieWinReceipts = buildSuccessHubOllieWinReceipts(
    findOllieFinding(
      ollieFindings,
      ollieVenueId,
      SUCCESS_HUB_OLLIE_WINS_CHECK_ID,
    ),
  )

  const openOlliePick = () => {
    if (!olliePick) return
    markOllieClicked(olliePick.finding, OLLIE_FINDING_SURFACES.SUCCESS_HUB)
    if (olliePick.cta.mode === SUCCESS_HUB_OLLIE_PICK_CTA_MODES.NAVIGATE) {
      history.push(olliePick.cta.path)
      return
    }
    openOllieWithFinding(olliePick.checkId, {
      startAction: olliePick.cta.action,
    })
  }
  const receipts = hub?.receipts || {
    recent: [],
    featuredCollection: null,
    collapsedCollections: [],
  }
  const recent = receipts.recent || []
  const featuredCollection = receipts.featuredCollection || null
  const collapsedCollections = receipts.collapsedCollections || []
  const tiles = hub?.tiles || []
  const missions = hub?.missions || []
  const setProgress = formatSuccessHubAchievementSetProgress(hub?.achievements)
  const matureBoard = isSuccessHubMatureBoard({
    score: hub?.score,
    blackBelt: hub?.blackBelt,
  })
  // 29.44 — mature / nearly-complete: status tiles, not a “Still open” restart.
  const matureTiles = isSuccessHubMatureTilesSection({
    score: hub?.score,
    blackBelt: hub?.blackBelt,
    setProgress,
  })
  const tilesCue = formatSuccessHubTilesCue({ matureTiles, setProgress })
  // One primary CTA on the job card — mature boards get a single leftover.
  const visibleMissions = missions.slice(0, matureBoard ? 1 : 2)
  const healthSummary = formatSuccessHubHealthFootnote(hub?.health)
  const momentum =
    loadState === 'ready'
      ? buildSuccessHubMomentumStrip({
          score: hub?.score,
          health: hub?.health,
          hero: hub?.hero,
          today: hub?.today,
          activationStages: hub?.activationStages,
          streaks: hub?.streaks,
          blackBelt: hub?.blackBelt,
        })
      : null
  const habitSummary =
    loadState === 'ready'
      ? buildSuccessHubProgressSummary({
          streaks: hub?.streaks,
          businessDays: hub?.businessDays,
          mature: matureBoard,
        })
      : null
  const hasReceipts =
    recent.length > 0 ||
    ollieWinReceipts.length > 0 ||
    Boolean(featuredCollection) ||
    collapsedCollections.length > 0
  const showQuietBlackBelt = showBlackBelt && !celebrateBlackBelt
  const showReceiptGallery = hasReceipts || showQuietBlackBelt
  // 29.46 — count quiet BB + recent as one gallery so a fourth isn’t orphaned.
  const receiptSlots = [
    ...(showQuietBlackBelt ? [{ type: 'blackBelt', key: 'black-belt' }] : []),
    ...ollieWinReceipts.map(item => ({
      type: 'ollieWin',
      key: item.id,
      item,
    })),
    ...recent.map(item => ({ type: 'achievement', key: item.id, item })),
  ]
  const receiptPlan = planSuccessHubReceiptGrid(receiptSlots.length)
  const visibleReceiptSlots = receiptSlots.slice(0, receiptPlan.visible)
  const receiptsMoreLabel = formatSuccessHubReceiptsMoreLabel(receiptPlan.more)
  // Score is only on the momentum strip. Health footnote only if the strip
  // is absent (empty Hub edge case) so points never outrank today’s job.
  const showHealthFootnote = Boolean(healthSummary) && !momentum

  const openTile = tile => {
    const action = successHubTileCtaAction(tile?.featureId)
    if (!venueId || !action) return
    if (
      shouldBlockStockStartCta({
        action,
        featureId: tile.featureId,
        plan: currentPlan,
      })
    ) {
      return
    }
    const ctaOptions = { fromProgress: true, featureId: tile.featureId }
    if (action === 'open_par_levels') {
      const staple = pickNextStapleForPar(venueProducts)
      if (staple) {
        ctaOptions.product = staple.title || staple.product?.title
        ctaOptions.productId = staple._id
      }
    }
    if (action === 'open_menu_builder') {
      ctaOptions.openCreate = true
    }
    const path = resolveGrowthEngineCtaPath(venueId, action, ctaOptions)
    if (path) history.push(path)
  }

  return (
    <FadeIn>
      <Page data-testid='progress-page'>
        <FirstPaint data-testid='progress-first-paint'>
          <SuccessHubProgressValueHero
            summary={habitSummary}
            daysEditor={
              loadState === 'ready' && hub?.businessDays ? (
                <SuccessHubBusinessDaysCard
                  venueId={venueId}
                  businessDays={hub.businessDays}
                  compact
                  quiet
                  embedded
                  testIdPrefix='progress'
                  onBusinessDaysChange={next =>
                    setHub(current =>
                      current ? { ...current, businessDays: next } : current,
                    )
                  }
                />
              ) : null
            }
          />
          <DesktopRow data-testid='progress-desktop-row'>
            <JobColumn data-testid='progress-hero'>
              {loadState === 'ready' ? (
                <JobLabelRow>
                  <Tooltip
                    title='The one useful kitchen job Progress recommends right now — same engine as missions below.'
                    mouseEnterDelay={0.3}
                  >
                    <SectionLabel data-testid='progress-today-label'>
                      Today’s kitchen job
                    </SectionLabel>
                  </Tooltip>
                  {olliePick ? (
                    <OlliePickTag
                      data-testid='progress-ollie-pick'
                      data-chrome='quiet'
                    >
                      <OllieIcon size={14} />
                      {olliePick.label}
                      {olliePick.asOfLabel ? (
                        <OlliePickMeta>· {olliePick.asOfLabel}</OlliePickMeta>
                      ) : null}
                    </OlliePickTag>
                  ) : null}
                </JobLabelRow>
              ) : null}
              {loadState === 'error' ? (
                <CaughtUp>
                  <BodyText>Couldn’t load today’s kitchen job.</BodyText>
                  <Button
                    type='link'
                    onClick={() => loadHub()}
                    data-testid='progress-retry'
                  >
                    Try again
                  </Button>
                </CaughtUp>
              ) : loadState !== 'ready' ? (
                <CaughtUp>
                  <BodyText>Loading today’s kitchen job…</BodyText>
                </CaughtUp>
              ) : heroCard ? (
                <HeroCard
                  title={heroCard.title}
                  body={heroCard.body}
                  featureId={heroCard.featureId}
                  ctaLabel={heroCard.ctaLabel}
                  estimatedMinutes={heroCard.estimatedMinutes}
                  honestEmptyNote={heroCard.honestEmptyNote}
                  showHumanPath={false}
                  onCta={
                    olliePick && isSuccessHubOlliePickAction(heroCard.ctaAction)
                      ? openOlliePick
                      : heroCard.ctaAction
                      ? () =>
                          recordAndGo({
                            ...heroCard,
                            cta: { action: heroCard.ctaAction },
                          })
                      : undefined
                  }
                  footerExtra={
                    showHeroAskOllie ? (
                      <Tooltip
                        title={
                          olliePick
                            ? 'Opens Ollie on what he noticed — ask why, or how to sort it.'
                            : 'Opens Ollie with a draft question about this job — edit it or send as-is.'
                        }
                        mouseEnterDelay={0.25}
                      >
                        <HeroAskOllie data-testid='progress-ask-ollie'>
                          <OllieAskLink
                            question={heroAskOllie.question}
                            checkId={olliePick?.checkId}
                            label={heroAskOllie.label}
                            onOpen={
                              olliePick
                                ? () =>
                                    markOllieClicked(
                                      olliePick.finding,
                                      OLLIE_FINDING_SURFACES.SUCCESS_HUB,
                                    )
                                : undefined
                            }
                          />
                        </HeroAskOllie>
                      </Tooltip>
                    ) : null
                  }
                  data-testid='progress-today-job'
                />
              ) : (
                <CaughtUp data-testid='progress-caught-up'>
                  <BodyText>You’re caught up on today’s kitchen job.</BodyText>
                </CaughtUp>
              )}
              {loadState === 'ready' ? (
                <HubNoticedRow
                  page={OLLIE_NOTICED_PAGES.SUCCESS_HUB}
                  surface={OLLIE_FINDING_SURFACES.SUCCESS_HUB}
                  excludeCheckIds={olliePick ? [olliePick.checkId] : []}
                  quiet
                />
              ) : null}
              {visibleMissions.length > 0 ? (
                <MissionPanel data-testid='progress-missions-panel'>
                  <Tooltip
                    title='Extra leftovers from the same recommendation engine — optional if you have a minute.'
                    mouseEnterDelay={0.3}
                  >
                    <SectionLabel data-testid='progress-missions-label'>
                      {matureBoard
                        ? 'One more if you have a minute'
                        : 'Also worth doing'}
                    </SectionLabel>
                  </Tooltip>
                  <Missions data-testid='progress-missions'>
                    {visibleMissions.map(mission => {
                      const missionCard = getNudgeCardPropsFromRecommendation(
                        mission,
                        { currentPlan },
                      )
                      const stockBlocked = shouldBlockStockStartCta({
                        action: mission?.cta?.action,
                        featureId: mission?.featureId,
                        plan: currentPlan,
                      })
                      const displayMission = stockBlocked
                        ? {
                            ...mission,
                            title: rewriteFreeStockDashboardCopy(mission.title),
                            body: rewriteFreeStockDashboardCopy(mission.body),
                            whyItMatters: rewriteFreeStockDashboardCopy(
                              mission.whyItMatters,
                            ),
                          }
                        : mission
                      return (
                        <SuccessHubMissionCard
                          key={mission.intentId}
                          mission={displayMission}
                          ctaLabel={missionCard?.ctaLabel}
                          ctaAction={missionCard?.ctaAction}
                          honestNote={
                            stockBlocked
                              ? getStockDashboardHonestNote(currentPlan)
                              : missionCard?.honestEmptyNote || null
                          }
                          onCta={
                            missionCard?.ctaLabel && missionCard?.ctaAction
                              ? () =>
                                  recordAndGo({
                                    ...mission,
                                    ctaAction: missionCard.ctaAction,
                                    cta: { action: missionCard.ctaAction },
                                  })
                              : undefined
                          }
                        />
                      )
                    })}
                  </Missions>
                </MissionPanel>
              ) : null}
            </JobColumn>

            {momentum ? (
              <MomentumColumn data-testid='progress-momentum-column'>
                <SuccessHubMomentumStrip
                  momentum={momentum}
                  hideNextJob
                  className='progress-momentum-aside'
                />
              </MomentumColumn>
            ) : null}
          </DesktopRow>
        </FirstPaint>

        {loadState === 'ready' &&
        shouldShowSuccessHubFoldCue({
          hasProof:
            showReceiptGallery ||
            tiles.length > 0 ||
            showCelebration ||
            celebrateBlackBelt,
          kitchenJob: olliePick || heroRecommendation,
        }) ? (
          <FoldCue data-testid='progress-fold-cue'>
            <FoldCopy data-testid='progress-fold-copy'>
              Done for today — proof below.
            </FoldCopy>
          </FoldCue>
        ) : null}

        <BoardBelow data-testid='progress-board-below'>
          {showCelebration ? (
            <SuccessHubCelebration celebration={hub.celebration} />
          ) : null}

          {celebrateBlackBelt ? (
            <SuccessHubBlackBelt blackBelt={hub.blackBelt} celebrate />
          ) : null}

          {showReceiptGallery || tiles.length > 0 ? (
            <LockedIn data-testid='progress-locked-in'>
              <Tooltip
                title='Kitchen wins you can lock in — each set has a clear count of how many are done and how many are left.'
                mouseEnterDelay={0.3}
              >
                <LockedInHeading data-testid='progress-locked-in-heading'>
                  Locked in
                </LockedInHeading>
              </Tooltip>
              <LockedInLead data-testid='progress-locked-in-lead'>
                {setProgress?.label ||
                  'Proof of wins and live counts — not another to-do list.'}
              </LockedInLead>
              {setProgress && setProgress.total > 0 ? (
                <SetBarTrack
                  role='progressbar'
                  aria-valuemin={0}
                  aria-valuemax={setProgress.total}
                  aria-valuenow={setProgress.earned}
                  aria-label={setProgress.label}
                  data-testid='progress-locked-in-set-bar'
                >
                  <SetBarFill
                    style={{
                      width: `${
                        (setProgress.earned / setProgress.total) * 100
                      }%`,
                    }}
                    $complete={setProgress.left === 0}
                  />
                </SetBarTrack>
              ) : null}

              {receiptSlots.length > 0 ? (
                <BadgeGrid
                  data-testid='progress-recent-achievements'
                  data-columns={receiptPlan.columns}
                  $columns={receiptPlan.columns}
                >
                  {visibleReceiptSlots.map(slot =>
                    slot.type === 'blackBelt' ? (
                      <SuccessHubBlackBelt
                        key={slot.key}
                        blackBelt={hub.blackBelt}
                        celebrate={false}
                      />
                    ) : slot.type === 'ollieWin' ? (
                      <SuccessHubBadge
                        key={slot.key}
                        variant='receipt'
                        badge={slot.item.badge}
                        title={slot.item.title}
                        earnedAt={slot.item.earnedAt}
                        whyItMatters={slot.item.whyItMatters}
                        data-testid='progress-ollie-win'
                      />
                    ) : (
                      <SuccessHubBadge
                        key={slot.key}
                        variant='receipt'
                        badge={slot.item.badge}
                        title={slot.item.displayName}
                        earnedAt={slot.item.earnedAt}
                        whyItMatters={slot.item.whyItMatters}
                        data-testid={`progress-badge-${slot.item.id}`}
                      />
                    ),
                  )}
                  {receiptsMoreLabel ? (
                    <MoreReceipts data-testid='progress-receipts-more'>
                      {receiptsMoreLabel}
                    </MoreReceipts>
                  ) : null}
                </BadgeGrid>
              ) : null}

              {featuredCollection || collapsedCollections.length > 0 ? (
                <SuccessHubCollections
                  featuredCollection={featuredCollection}
                  collapsedCollections={collapsedCollections}
                />
              ) : null}

              {tiles.length > 0 ? (
                <>
                  {tilesCue ? (
                    <TilesCue
                      data-testid='progress-tiles-cue'
                      data-mature={matureTiles ? 'true' : 'false'}
                    >
                      {tilesCue}
                    </TilesCue>
                  ) : null}
                  <TileGrid
                    data-testid='progress-tiles'
                    data-mature={matureTiles ? 'true' : 'false'}
                  >
                    {/* Express already hid tiles the role cannot open — render as sent. */}
                    {tiles.map(tile => {
                      const action = successHubTileCtaAction(tile.featureId)
                      const stockBlocked = shouldBlockStockStartCta({
                        action,
                        featureId: tile.featureId,
                        plan: currentPlan,
                      })
                      const clickable = Boolean(action) && !stockBlocked
                      const tip = [
                        tile.whyItMatters || tile.outcome,
                        formatSuccessHubTileStat(tile),
                        clickable
                          ? matureTiles
                            ? 'Opens the feature for detail.'
                            : 'Opens the feature if you want the detail.'
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                      const tileNode = (
                        <Tile
                          as={clickable ? 'button' : 'div'}
                          type={clickable ? 'button' : undefined}
                          onClick={clickable ? () => openTile(tile) : undefined}
                          $clickable={clickable}
                          $quiet={matureTiles}
                          data-testid={`progress-tile-${tile.featureId}`}
                          data-quiet={matureTiles ? 'true' : undefined}
                        >
                          <QuietTileMark $quiet={matureTiles}>
                            <SuccessHubBadge
                              badge={tile.badge}
                              title={tile.outcome}
                              size={SUCCESS_HUB_BADGE_MARK_SIZE.tile}
                            />
                          </QuietTileMark>
                          <TileCopy>
                            <TileOutcome>
                              {tile.outcome || tile.whyItMatters}
                            </TileOutcome>
                            <TileStat
                              data-testid={`progress-tile-${tile.featureId}-stat`}
                            >
                              {formatSuccessHubTileStat(tile)}
                            </TileStat>
                          </TileCopy>
                        </Tile>
                      )
                      return (
                        <Tooltip
                          key={tile.featureId}
                          title={tip || undefined}
                          mouseEnterDelay={0.25}
                        >
                          {tileNode}
                        </Tooltip>
                      )
                    })}
                  </TileGrid>
                </>
              ) : null}
            </LockedIn>
          ) : null}

          {loadState === 'ready' && showHealthFootnote ? (
            <Footnote data-testid='progress-health-footnote'>
              <Health data-testid='progress-health-line'>
                {healthSummary}
              </Health>
            </Footnote>
          ) : null}
        </BoardBelow>
      </Page>
    </FadeIn>
  )
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme?.spacing?.lg || '28px'};
  margin: 0 auto;
  max-width: 72rem;
  padding-bottom: ${({ theme }) => theme?.spacing?.xl || '40px'};
  width: 100%;
`

const FirstPaint = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme?.spacing?.md || '18px'};
  width: 100%;
`

const DesktopRow = styled.div`
  /* 29.45 — stretch columns so a short % card doesn’t leave a grey void. */
  align-items: stretch;
  display: grid;
  gap: ${({ theme }) => theme?.spacing?.md || '20px'};
  width: 100%;

  @media (min-width: 960px) {
    /* 60/40 — job column owns the ask; momentum shares the same page row. */
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  }
`

const JobColumn = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme?.spacing?.md || '16px'};
  min-width: 0;
  width: 100%;
`

const MomentumColumn = styled.aside`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;

  /* Intentional fill: mayo strip matches job-column height on desktop. */
  .progress-momentum-aside {
    flex: 1 1 auto;
    height: 100%;
    max-width: none;
    min-height: 100%;
    width: 100%;
  }
`

const JobLabelRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
`

const OlliePickTag = styled.span`
  align-items: center;
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  display: inline-flex;
  font-size: ${T.type.micro};
  font-weight: 700;
  gap: 4px;
  line-height: 1.3;
`

const OlliePickMeta = styled.span`
  font-weight: 400;
`

const HubNoticedRow = styled(OllieNoticedRows)`
  margin: 0;
`

const SectionLabel = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  cursor: help;
  font-size: ${T.type.micro};
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.3;
  margin: 0;
  width: fit-content;
`

const BoardBelow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space.lg};
  width: 100%;
`

const FoldCue = styled.div`
  border-top: 1px solid
    ${({ theme }) => theme?.colours?.chipGrey || T.borderColourFallback};
  padding: ${T.space.sm} 0 0;
  width: 100%;
`

const FoldCopy = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: ${T.type.meta};
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.35;
  margin: 0;
  width: 100%;
`

const HeroCard = styled(GrowthEngineNudgeCard)`
  border-left-width: 4px;
  border-radius: ${T.radius.surface};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  max-width: none;
  padding: ${T.pad.job};
  width: 100%;

  /* Loudest content type on Progress — only primary lime CTA on this card. */
  [data-testid='nudge-title'] {
    font-size: ${T.type.jobTitle};
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  [data-testid='nudge-body'] {
    font-size: ${T.type.heading};
    line-height: 1.55;
  }

  [data-testid='nudge-cta'] {
    font-size: 15px;
    font-weight: 700;
    height: auto;
    min-height: 40px;
    padding: 8px 18px;
  }
`

const HeroAskOllie = styled.div`
  flex: 1 1 100%;
  min-width: 0;
  padding-top: 2px;

  button {
    margin: 0;
    padding-left: 0;
  }
`

const CaughtUp = styled.div`
  width: 100%;
`

const MissionPanel = styled.section`
  background: ${({ theme }) => theme?.colours?.mayoWhite || '#fff'};
  border: ${({ theme }) => successHubProgressSurfaceBorder(theme)};
  border-radius: ${T.radius.surface};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${T.pad.surface};
  width: 100%;
`

const Missions = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
`

const LockedIn = styled.section`
  background: ${({ theme }) => theme?.colours?.mayoWhite || '#fff'};
  border: ${({ theme }) => successHubProgressSurfaceBorder(theme)};
  border-radius: ${T.radius.surface};
  display: flex;
  flex-direction: column;
  gap: ${T.space.md};
  padding: ${T.pad.surface};
  width: 100%;
`

const LockedInHeading = styled.h3`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  cursor: help;
  font-size: ${T.type.heading};
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
  width: fit-content;
`

const LockedInLead = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: ${T.type.lead};
  font-weight: 500;
  line-height: 1.4;
  margin: -8px 0 0;
`

const SetBarTrack = styled.div`
  background: ${({ theme }) => theme?.colours?.paleGrey || '#f7f7f7'};
  border-radius: 999px;
  height: ${T.meter.set};
  margin-top: -4px;
  overflow: hidden;
  width: 100%;
`

const SetBarFill = styled.div`
  background: ${({ theme, $complete }) =>
    $complete
      ? theme?.colours?.oliveGrey || '#8c8c8c'
      : theme?.colours?.bbqBlack || '#262626'};
  border-radius: 999px;
  height: 100%;
  max-width: 100%;
  min-width: 0;
  opacity: ${({ $complete }) => ($complete ? 0.55 : 0.85)};
  transition: width 0.25s ease;
`

const BadgeGrid = styled.div`
  display: grid;
  gap: ${T.space.sm} ${T.space.md};
  /* 29.46 — explicit columns: ≤3 share one row; 4 (or 3+more) use 2×2. */
  grid-template-columns: ${({ $columns }) =>
    $columns === 2
      ? 'repeat(2, minmax(0, 1fr))'
      : $columns === 3
      ? 'repeat(3, minmax(0, 1fr))'
      : 'minmax(0, 1fr)'};
  width: 100%;

  @media (max-width: 639px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const MoreReceipts = styled.div`
  align-items: center;
  background: ${({ theme }) => theme?.colours?.paleGrey || '#f7f7f7'};
  border: ${successHubProgressRowBorder()};
  border-radius: ${T.radius.row};
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  display: flex;
  font-size: ${T.type.rowMeta};
  font-weight: 600;
  justify-content: center;
  line-height: 1.35;
  min-height: 4.5rem;
  padding: ${T.pad.row};
`

const TilesCue = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: ${T.type.lead};
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const TileGrid = styled.div`
  display: grid;
  gap: ${T.space.sm};
  grid-template-columns: repeat(auto-fill, minmax(15.5rem, 1fr));
  width: 100%;
`

/** 29.44 — desaturate category colour so mature tiles don’t read as status coding. */
const QuietTileMark = styled.div`
  flex-shrink: 0;
  ${({ $quiet }) =>
    $quiet
      ? `
    filter: saturate(0.4);
    opacity: 0.7;
  `
      : ''}
`

const Tile = styled.div`
  align-items: flex-start;
  background: ${({ theme }) => theme?.colours?.paleGrey || '#f7f7f7'};
  border: ${successHubProgressRowBorder()};
  border-radius: ${T.radius.row};
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  display: flex;
  font: inherit;
  gap: ${T.space.sm};
  padding: ${T.pad.row};
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
  width: 100%;

  ${({ $quiet }) =>
    $quiet
      ? `
    background: transparent;
  `
      : ''}

  ${({ $clickable, theme }) =>
    $clickable
      ? `
    &:hover {
      background: ${theme?.colours?.mayoWhite || '#fff'};
      border-color: ${theme?.colours?.chipGrey || 'rgba(0, 0, 0, 0.08)'};
    }
    &:focus-visible {
      outline: 2px solid ${theme?.colours?.oliveGrey || '#8c8c8c'};
      outline-offset: 2px;
    }
  `
      : ''}
`

const TileCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const TileOutcome = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.rowMeta};
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const TileStat = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#919191'};
  font-size: ${T.type.meta};
  line-height: 1.4;
  margin: 0;
`

const Footnote = styled.footer`
  border-top: 1px solid ${({ theme }) => theme?.colours?.panSilver || '#e0dfdf'};
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: ${({ theme }) => theme?.spacing?.xs || '12px'};
`

const Health = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#919191'};
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
`

export default Progress
